/**
 * useAccountRoutines — the ONE owner of the account's `listRoutines()`
 * snapshot (routine list parity, 2026-08-02).
 *
 * Hosted ONCE in ThreadSidebar (the sole mount point of both routine
 * surfaces) so the rail's "routines" section and the RoutineManagerPanel
 * consume the SAME instance — membership and count parity by construction
 * (see routineRows.ts, the shared row builder both feed these state into).
 *
 * Refresh triggers (each caller invokes `refresh()`):
 *   • HOOK MOUNT — the sidebar (and with it the routines section) becoming
 *     visible fetches once, UNLESS the cross-mount snapshot cache below is
 *     still warm for this account (fullscreen toggle remount, finding 5);
 *   • MANAGER OPEN — the panel refreshes on open (preserving the original
 *     fresh-on-open behavior);
 *   • ACTION SUCCESS — pause/resume/stop keep their optimistic patch
 *     (patchRoutine/removeRoutine below) AND refresh for server truth;
 *   • ROUTINE THREAD DEPARTURE — when a thread with `routineId` leaves the
 *     local list (archived here or elsewhere, or aged out of the capped
 *     listen window), the effect below refreshes so a server-stopped routine
 *     leaves both lists immediately — and a merely-aged-out one is covered
 *     by a fresh server row without waiting for the next open.
 *
 * COALESCE + TRAILING RUN (adversarial review finding 1, 2026-08-03). The
 * original dedupe returned the in-flight promise and scheduled NOTHING, so a
 * trigger that fired DURING a flight was silently discarded and the landing
 * snapshot predated the state change that asked for it. Deterministic repro:
 * open the manager (fetch F1 starts) → archive a routine thread inside that
 * window → the departure effect's refresh() coalesced onto F1 → F1 landed the
 * PRE-archive list → the archived routine (no thread record anymore) rendered
 * as a server-only row in BOTH surfaces until remount. Now a refresh() during
 * a flight sets `pendingRefreshRef` and gets a promise that resolves after the
 * FOLLOW-UP run; the flight's `finally` starts exactly one more fetch, so the
 * LAST requester's state is always reflected. Bounded by construction: the
 * trailing run only happens when an EXTERNAL refresh() set the flag during
 * the previous flight (the flag is cleared before the follow-up starts), so
 * it cannot self-perpetuate.
 *
 * The trailing run is UNCONDITIONAL — there is no way for this hook to know
 * whether a caller's motivating state change made it into the in-flight
 * request, so it must assume it did not. "Hook mount + manager open in the
 * same commit = ONE Cloud Function invocation" is therefore preserved on the
 * CALLER side instead: RoutineManagerPanel's fresh-on-open skips its refresh
 * while `fetchState === 'loading'`, because an in-flight fetch is already
 * about to land fresh data (see that component's effect).
 *
 * Failure semantics (adversarial review finding 2, 2026-08-03) — a transient
 * failure must NOT collapse the rail. Since the fetch moved out of the panel
 * into the rail, blanking `routines` on an offline blip / 5xx / the 25s
 * timeout dropped the sidebar back to thread-only rows ("1 routine" again)
 * with no error affordance and no retry there. So: the last SUCCESSFUL
 * snapshot is retained (`routines` stays null only while no fetch has ever
 * succeeded), `fetchState` still goes 'error' so the panel's existing inline
 * notice fires, and ONE bounded automatic retry is scheduled
 * (ACCOUNT_ROUTINES_RETRY_DELAY_MS, cancelled on unmount, never chained — a
 * failed retry does not schedule another). Retaining a stale snapshot is safe
 * for the archive case because ThreadSidebar filters locally-tombstoned ids
 * out of the union (finding 4).
 *
 * CROSS-MOUNT SNAPSHOT CACHE (adversarial review finding 5, 2026-08-03).
 * GlobalChatSystem mounts ThreadSidebar conditionally on fullscreen, so every
 * fullscreen exit/entry remounts this hook — a fresh listRoutines() per toggle
 * and a visible routines-count flicker (1 → 4) on each entry. The last good
 * snapshot is therefore cached at MODULE level, above the component lifetime,
 * for ACCOUNT_ROUTINES_CACHE_TTL_MS. The cache is scoped to the signed-in uid
 * (`args.uid`) and is DISABLED entirely without one, so switching accounts can
 * never show another account's routines; patch/remove (pause/resume/stop)
 * invalidate it so a remount can never resurrect pre-action state.
 *
 * DRIFT HEAL (2026-08-01, moved here from the panel): the list is built from
 * the authoritative routine docs. Where it contradicts a live thread's
 * frozen mirror STATUS, the host record is patched too (local-only, via
 * onRoutineMirrorPatch) so the rail row and header agree with the manager —
 * status + its travelling nextRunAt only.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ChatThread } from './types';
import type { ThreadRoutineMirrorPatch } from './useChatThreads';
import { msToIso } from './routineRows';
import {
  listRoutines,
  type RoutineActionResult,
  type RoutineSummary,
} from '../../services/routines/client';

export type AccountRoutinesFetchState = 'loading' | 'done' | 'error';

/**
 * How long a successful snapshot survives ACROSS mounts (finding 5). Long
 * enough that a fullscreen toggle round-trip reuses it (no flicker, no extra
 * Cloud Function invocation), short enough that a genuinely new visit fetches.
 */
export const ACCOUNT_ROUTINES_CACHE_TTL_MS = 30_000;

/**
 * Delay before the SINGLE automatic retry after a failed fetch (finding 2).
 * One retry only — long enough to clear a blip, never a poll loop.
 */
export const ACCOUNT_ROUTINES_RETRY_DELAY_MS = 3_000;

interface AccountRoutinesCacheEntry {
  /** Owning account (never empty — an empty scope disables caching). */
  uid: string;
  routines: RoutineSummary[];
  fetchedAtMs: number;
}

let cachedSnapshot: AccountRoutinesCacheEntry | null = null;

/**
 * Cache scope for a uid: the trimmed uid, or null meaning "do not cache".
 * Signed-out/unknown sessions have no account routines to speak of, and an
 * unscoped cache could only ever leak — so they opt out entirely.
 */
function accountRoutinesCacheScope(uid?: string | null): string | null {
  const key = (uid ?? '').trim();
  return key ? key : null;
}

/** The warm snapshot for this account, or null (absent / foreign / expired). */
export function readAccountRoutinesCache(
  uid?: string | null,
  nowMs: number = Date.now(),
): RoutineSummary[] | null {
  const scope = accountRoutinesCacheScope(uid);
  if (!scope || !cachedSnapshot) return null;
  if (cachedSnapshot.uid !== scope) return null;
  if (nowMs - cachedSnapshot.fetchedAtMs > ACCOUNT_ROUTINES_CACHE_TTL_MS) return null;
  return cachedSnapshot.routines;
}

function writeAccountRoutinesCache(uid: string | null, routines: RoutineSummary[]): void {
  const scope = accountRoutinesCacheScope(uid);
  if (!scope) return;
  cachedSnapshot = { uid: scope, routines, fetchedAtMs: Date.now() };
}

/**
 * Drop the cross-mount snapshot. Called after every optimistic mutation
 * (pause/resume/stop) so a remount inside the TTL refetches instead of
 * replaying pre-action state; exported for tests and for hosts that need to
 * force a cold read (e.g. sign-out).
 */
export function invalidateAccountRoutinesCache(): void {
  cachedSnapshot = null;
}

/**
 * The shared account-routines surface both routine renderers consume. Passed
 * BY VALUE into RoutineManagerPanel (one hook instance, one snapshot).
 */
export interface AccountRoutinesApi {
  /**
   * null until the first successful fetch. A LATER failure keeps the last
   * successful snapshot (finding 2) — check `fetchState` for freshness.
   */
  routines: RoutineSummary[] | null;
  fetchState: AccountRoutinesFetchState;
  /**
   * Re-fetch the account list. Never rejects (failures land in fetchState).
   * A call made while a fetch is in flight coalesces onto ONE follow-up run
   * and resolves when THAT run lands, so the caller's own state change is
   * always reflected in the snapshot it awaits (finding 1).
   */
  refresh: () => Promise<void>;
  /**
   * Optimistically patch a fetched row after a successful pause/resume —
   * server-only rows have no subscription echo, and a stale status here
   * would beat the thread mirror through the union's status rule. Prefers
   * the action response's echo (fresh nextRunAtMs after a resume).
   */
  patchRoutine: (
    threadId: string,
    status: RoutineSummary['status'],
    routine?: RoutineActionResult['routine'],
  ) => void;
  /** Drop a routine from the fetched list after a successful cancel. */
  removeRoutine: (threadId: string) => void;
}

export interface UseAccountRoutinesArgs {
  /** The live thread list — drives the departure-refresh + drift heal. */
  threads: ChatThread[];
  /**
   * Signed-in uid. ONLY used to scope the cross-mount snapshot cache
   * (finding 5) — absent/empty simply disables caching, so the hook behaves
   * exactly as before for guests and in isolated tests.
   */
  uid?: string | null;
  /**
   * Host patch into the LOCAL thread record's routine mirror
   * (threadStore.patchThreadRoutineMirror) — used by the drift heal when the
   * authoritative snapshot contradicts a frozen mirror status. Optional; the
   * union's status rule keeps ROWS correct without it.
   */
  onRoutineMirrorPatch?: (threadId: string, patch: ThreadRoutineMirrorPatch) => void;
}

export function useAccountRoutines(args: UseAccountRoutinesArgs): AccountRoutinesApi {
  const uidScope = accountRoutinesCacheScope(args.uid);
  // Warm start from the cross-mount cache (finding 5): a remount inside the
  // TTL renders the previous rows immediately — no 1 → 4 count flicker.
  const [routines, setRoutines] = useState<RoutineSummary[] | null>(
    () => readAccountRoutinesCache(uidScope),
  );
  const [fetchState, setFetchState] = useState<AccountRoutinesFetchState>(
    () => (readAccountRoutinesCache(uidScope) ? 'done' : 'loading'),
  );

  // Latest inputs by ref so refresh() stays identity-stable (effects keyed on
  // it must fire exactly once) while always healing against fresh threads.
  const threadsRef = useRef(args.threads);
  threadsRef.current = args.threads;
  const onRoutineMirrorPatchRef = useRef(args.onRoutineMirrorPatch);
  onRoutineMirrorPatchRef.current = args.onRoutineMirrorPatch;
  const uidScopeRef = useRef(uidScope);
  uidScopeRef.current = uidScope;

  const mountedRef = useRef(true);
  /** The single pending auto-retry (finding 2) — cleared on unmount. */
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearRetryTimer = useCallback(() => {
    if (retryTimerRef.current === null) return;
    clearTimeout(retryTimerRef.current);
    retryTimerRef.current = null;
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearRetryTimer();
    };
  }, [clearRetryTimer]);

  /** In-flight dedupe: mount fetch + same-commit manager open = ONE request. */
  const inFlightRef = useRef<Promise<void> | null>(null);
  /** A refresh() arrived mid-flight → run exactly one more when it lands. */
  const pendingRefreshRef = useRef(false);
  /** Callers waiting on that follow-up run (see refresh()). */
  const trailingWaitersRef = useRef<Array<() => void>>([]);
  /** Self-reference so a run's `finally` can start the follow-up run. */
  const startRunRef = useRef<((isAutoRetry: boolean) => Promise<void>) | null>(null);

  const startRun = useCallback((isAutoRetry: boolean): Promise<void> => {
    // Any deliberate run supersedes a queued retry.
    clearRetryTimer();
    const run = (async () => {
      if (mountedRef.current) setFetchState('loading');
      try {
        const fetched = await listRoutines();
        if (!mountedRef.current) return;
        setRoutines(fetched);
        setFetchState('done');
        writeAccountRoutinesCache(uidScopeRef.current, fetched);
        // DRIFT HEAL (2026-08-01, see module doc): status + travelling
        // nextRunAt only, and only where server truth actually disagrees.
        // v2 aggregate rule (a thread can hold several routines): the
        // mirror's routineStatus means "any routine here is active", so the
        // heal compares against the thread's WHOLE server row set.
        const patch = onRoutineMirrorPatchRef.current;
        if (patch) {
          const byThread = new Map<string, typeof fetched>();
          for (const s of fetched) {
            const list = byThread.get(s.threadId);
            if (list) list.push(s);
            else byThread.set(s.threadId, [s]);
          }
          for (const t of threadsRef.current) {
            if (!t.routineId) continue;
            const serverRows = byThread.get(t.id);
            if (!serverRows || serverRows.length === 0) continue;
            const anyActive = serverRows.some((s) => s.status === 'active');
            if (!anyActive === (t.routineStatus === 'paused')) continue;
            const soonest = serverRows
              .filter((s) => s.status === 'active' && typeof s.nextRunAtMs === 'number')
              .map((s) => s.nextRunAtMs as number)
              .sort((a, b) => a - b)[0];
            patch(t.id, {
              routineStatus: anyActive ? 'active' : 'paused',
              routineNextRunAt: msToIso(soonest ?? null),
            });
          }
        }
      } catch {
        if (!mountedRef.current) return;
        // FINDING 2: degraded, NOT blanked. `routines` is deliberately left
        // untouched so the last successful snapshot survives a blip (it is
        // still null when no fetch has ever succeeded, which is the original
        // degraded behavior the panel's notice + rail fallback expect).
        setFetchState('error');
        if (!isAutoRetry) {
          retryTimerRef.current = setTimeout(() => {
            retryTimerRef.current = null;
            if (!mountedRef.current) return;
            void startRunRef.current?.(true);
          }, ACCOUNT_ROUTINES_RETRY_DELAY_MS);
        }
      } finally {
        inFlightRef.current = null;
        // FINDING 1: exactly one trailing run per burst, and the callers that
        // asked for it resolve only once THAT run has landed.
        if (pendingRefreshRef.current) {
          pendingRefreshRef.current = false;
          const waiters = trailingWaitersRef.current;
          trailingWaitersRef.current = [];
          const settle = () => waiters.forEach((resolve) => resolve());
          if (mountedRef.current && startRunRef.current) {
            void startRunRef.current(false).then(settle, settle);
          } else {
            settle();
          }
        }
      }
    })();
    inFlightRef.current = run;
    return run;
  }, [clearRetryTimer]);
  startRunRef.current = startRun;

  const refresh = useCallback((): Promise<void> => {
    if (inFlightRef.current) {
      pendingRefreshRef.current = true;
      return new Promise<void>((resolve) => {
        trailingWaitersRef.current.push(resolve);
      });
    }
    return startRun(false);
  }, [startRun]);

  // Fetch once on mount — the routines section just became visible — unless
  // the cross-mount cache is still warm for this account (finding 5). Also
  // handles a mid-life account switch: the previous account's rows are
  // dropped immediately, never rendered under the new uid.
  const lastUidScopeRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const previous = lastUidScopeRef.current;
    const accountChanged = previous !== undefined && previous !== uidScope;
    lastUidScopeRef.current = uidScope;
    const cached = readAccountRoutinesCache(uidScope);
    if (cached) {
      setRoutines(cached);
      setFetchState('done');
      return;
    }
    if (accountChanged) {
      setRoutines(null);
      setFetchState('loading');
    }
    void refresh();
  }, [uidScope, refresh]);

  // ROUTINE THREAD DEPARTURE → refresh (archive-parity trigger, see module
  // doc). Set-shrink detection only — snapshot churn that keeps the same
  // routine membership never fetches.
  const prevRoutineIdsRef = useRef<Set<string> | null>(null);
  useEffect(() => {
    const current = new Set(
      args.threads.filter((t) => !!t.routineId).map((t) => t.id),
    );
    const prev = prevRoutineIdsRef.current;
    prevRoutineIdsRef.current = current;
    if (!prev) return;
    let departed = false;
    prev.forEach((id) => {
      if (!current.has(id)) departed = true;
    });
    if (departed) void refresh();
  }, [args.threads, refresh]);

  const patchRoutine = useCallback((
    routineId: string,
    status: RoutineSummary['status'],
    routine?: RoutineActionResult['routine'],
  ) => {
    // The cross-mount cache holds PRE-action truth — drop it (finding 5).
    invalidateAccountRoutinesCache();
    setRoutines((prev) => {
      if (!prev) return prev;
      return prev.map((s) => {
        if (s.routineId !== routineId) return s;
        const fresh = routine && (routine.routineId === routineId || routine.threadId === s.threadId)
          ? routine
          : null;
        return {
          ...s,
          status,
          // Prefer the response echo when present — a resume recomputes
          // nextRunAtMs server-side and the echo carries the fresh value.
          ...(fresh
            ? {
                ...(fresh.name ? { name: fresh.name } : {}),
                ...(fresh.scheduleSummary ? { scheduleSummary: fresh.scheduleSummary } : {}),
                nextRunAtMs: fresh.nextRunAtMs ?? null,
              }
            : {}),
        };
      });
    });
  }, []);

  const removeRoutine = useCallback((routineId: string) => {
    invalidateAccountRoutinesCache();
    setRoutines((prev) => (prev ? prev.filter((s) => s.routineId !== routineId) : prev));
  }, []);

  return { routines, fetchState, refresh, patchRoutine, removeRoutine };
}

export default useAccountRoutines;
